/**
 * The review screen's logic, pure: filter, sort, select, spot duplicates of existing expenses, and suggest a payee and
 * category from past expenses. Money is integer minor units throughout; nothing here rounds.
 */
import { dayNumber, type Direction, type Txn } from "./statement";

// ---- filter and sort ------------------------------------------------------------------------------------

export interface Filters {
  direction: Direction | "all";
  /** YYYY-MM-DD, inclusive; "" = open. */
  from: string;
  to: string;
  /** Minor units, inclusive; null = open. */
  min: number | null;
  max: number | null;
  q: string;
}

export const defaultFilters = (): Filters => ({ direction: "debit", from: "", to: "", min: null, max: null, q: "" });

export const isFiltered = (f: Filters) => f.direction !== "debit" || f.from !== "" || f.to !== "" || f.min !== null || f.max !== null || f.q.trim() !== "";

/** How many filters beyond the default Debit are set (the badge on the mobile Filters button). */
export const activeFilterCount = (f: Filters) => (f.direction !== "debit" ? 1 : 0) + (f.from || f.to ? 1 : 0) + (f.min !== null || f.max !== null ? 1 : 0) + (f.q.trim() ? 1 : 0);

export function applyFilters<T extends Txn>(rows: readonly T[], f: Filters): T[] {
  const terms = f.q.toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((t) => {
    if (f.direction !== "all" && t.direction !== f.direction) return false;
    if (f.from && t.date < f.from) return false;
    if (f.to && t.date > f.to) return false;
    if (f.min !== null && t.amount < f.min) return false;
    if (f.max !== null && t.amount > f.max) return false;
    if (terms.length) {
      const hay = `${t.description} ${t.ref ?? ""}`.toLowerCase();
      if (!terms.every((x) => hay.includes(x))) return false;
    }
    return true;
  });
}

export type SortKey = "date" | "amount" | "description";
export interface Sort {
  key: SortKey;
  dir: "asc" | "desc";
}

/** Stable: ties fall back to the order in the statement. */
export function sortRows<T extends Txn>(rows: readonly T[], s: Sort): T[] {
  const sign = s.dir === "asc" ? 1 : -1;
  const cmp = (a: T, b: T) =>
    s.key === "date" ? (a.date < b.date ? -1 : a.date > b.date ? 1 : 0) : s.key === "amount" ? a.amount - b.amount : a.description.localeCompare(b.description, "en", { sensitivity: "base" });
  return rows.map((r, i) => ({ r, i })).sort((x, y) => sign * cmp(x.r, y.r) || x.r.rowNo - y.r.rowNo || x.i - y.i).map((x) => x.r);
}

// ---- selection ------------------------------------------------------------------------------------------

/** Only money out can be an expense. */
export const isSelectable = (t: Txn) => t.direction === "debit";

export type Dups = ReadonlyMap<string, DupInfo>;

/**
 * "Select all" over the filtered rows: debits that are not possible duplicates and not already imported.
 * A possible duplicate can still be ticked by hand.
 */
export function selectAllFiltered(current: ReadonlySet<string>, filtered: readonly Txn[], dups: Dups, imported: ReadonlySet<string> = new Set()): Set<string> {
  const next = new Set(current);
  for (const t of filtered) if (isSelectable(t) && !dups.has(t.id) && !imported.has(t.id)) next.add(t.id);
  return next;
}

/** Clears the filtered rows from the selection; selections hidden by the filter stay. */
export function clearFiltered(current: ReadonlySet<string>, filtered: readonly Txn[]): Set<string> {
  const next = new Set(current);
  for (const t of filtered) next.delete(t.id);
  return next;
}

export function toggle(current: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export interface SelectionSummary {
  count: number;
  /** Sum in minor units. */
  total: number;
}

export function summarise(rows: readonly Txn[], selected: ReadonlySet<string>): SelectionSummary {
  let count = 0;
  let total = 0;
  for (const t of rows) {
    if (selected.has(t.id)) {
      count++;
      total += t.amount;
    }
  }
  return { count, total };
}

// ---- duplicates -----------------------------------------------------------------------------------------

export interface ExistingExpense {
  id: string;
  date: string;
  total: number;
  reference: string | null;
}

export interface DupInfo {
  kind: "reference" | "date_amount";
  expenseId: string;
}

/** A bank reference worth trusting: 6+ characters, not all zeros. Same rule as the server's `usableBankRef`. */
export function usableRef(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const r = ref.trim().replace(/\s+/g, "").toUpperCase();
  return r.length < 6 || /^0+$/.test(r) ? null : r;
}

/**
 * A debit is a possible duplicate of an existing expense with the same bank reference, or the same amount on the same day
 * or a day either side. Credits are never expenses, so they are never flagged.
 */
export function findDuplicates(txns: readonly Txn[], existing: readonly ExistingExpense[]): Map<string, DupInfo> {
  const byRef = new Map<string, ExistingExpense>();
  const byAmount = new Map<number, Array<ExistingExpense & { day: number }>>();
  for (const e of existing) {
    const ref = usableRef(e.reference);
    if (ref && !byRef.has(ref)) byRef.set(ref, e);
    const list = byAmount.get(e.total) ?? [];
    list.push({ ...e, day: dayNumber(e.date) });
    byAmount.set(e.total, list);
  }
  const out = new Map<string, DupInfo>();
  for (const t of txns) {
    if (t.direction !== "debit") continue;
    const ref = usableRef(t.ref);
    const hit = ref ? byRef.get(ref) : undefined;
    if (hit) {
      out.set(t.id, { kind: "reference", expenseId: hit.id });
      continue;
    }
    const day = dayNumber(t.date);
    const near = byAmount.get(t.amount)?.find((e) => Math.abs(e.day - day) <= 1);
    if (near) out.set(t.id, { kind: "date_amount", expenseId: near.id });
  }
  return out;
}

// ---- suggestions ----------------------------------------------------------------------------------------

export interface HistoryEntry {
  categoryId: string;
  payeeId: string | null;
  payeeName: string | null;
  note: string | null;
  paymentMode: string;
}

export interface Suggestion {
  categoryId: string;
  payeeId: string | null;
  payeeName: string | null;
}

const STOP = new Set([
  "upi", "neft", "imps", "rtgs", "ach", "nach", "ecs", "pos", "atm", "ref", "payment", "pay", "paid", "transfer", "trf", "tfr", "from", "via", "debit", "credit", "card", "purchase", "txn", "transaction",
  "the", "and", "for", "cash", "chq", "cheque", "mob", "mobile", "banking", "bank", "ltd", "limited", "pvt", "private", "india", "inb", "ibl", "ybl", "oksbi", "okaxis", "okhdfcbank", "okicici", "paid",
]);

const HANDLE = /[a-z0-9._]{2,}@[a-z][a-z0-9]{1,}/g;

/** Words worth matching on in a narration: UPI handles (strong), and plain words that are not bank jargon, ids or codes. */
export function narrationTokens(text: string): { handles: string[]; words: string[] } {
  const lower = text.toLowerCase();
  const handles = [...new Set(lower.match(HANDLE) ?? [])];
  const rest = lower.replace(HANDLE, " ");
  const words = rest
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !/\d{4,}/.test(w) && !/^\d+$/.test(w) && !STOP.has(w));
  const fromHandles = handles.map((h) => h.split("@")[0]!.replace(/[^a-z]/g, "")).filter((w) => w.length >= 3 && !STOP.has(w));
  return { handles, words: [...new Set([...words, ...fromHandles])] };
}

type Combo = { categoryId: string; payeeId: string | null; payeeName: string | null; count: number };

/**
 * Learns from past expenses: every handle or word in a past expense's narration (stored as its note) or payee name votes
 * for that expense's category and payee. A word seen under many different payees counts for less.
 */
export function buildSuggester(history: readonly HistoryEntry[], payeeNames: ReadonlyMap<string, string>) {
  const index = new Map<string, Map<string, Combo>>();
  for (const h of history) {
    const payee = h.payeeId ? (payeeNames.get(h.payeeId) ?? h.payeeName) : h.payeeName;
    const { handles, words } = narrationTokens(`${h.note ?? ""} ${payee ?? ""}`);
    const comboKey = `${h.categoryId}|${h.payeeId ?? ""}|${(h.payeeId ? "" : (h.payeeName ?? "")).toLowerCase()}`;
    for (const tok of new Set([...handles.map((x) => `@${x}`), ...words])) {
      const m = index.get(tok) ?? new Map<string, Combo>();
      const c = m.get(comboKey) ?? { categoryId: h.categoryId, payeeId: h.payeeId, payeeName: h.payeeId ? null : h.payeeName, count: 0 };
      c.count++;
      m.set(comboKey, c);
      index.set(tok, m);
    }
  }
  return (narration: string): Suggestion | null => {
    const { handles, words } = narrationTokens(narration);
    const scores = new Map<string, { combo: Combo; score: number }>();
    const vote = (tok: string, weight: number) => {
      const m = index.get(tok);
      if (!m) return;
      for (const [key, combo] of m) {
        const s = scores.get(key) ?? { combo, score: 0 };
        s.score += (weight * combo.count) / m.size;
        scores.set(key, s);
      }
    };
    for (const h of handles) vote(`@${h}`, 4);
    for (const w of words) vote(w, 1);
    let best: { combo: Combo; score: number } | null = null;
    for (const s of scores.values()) if (!best || s.score > best.score) best = s;
    return best ? { categoryId: best.combo.categoryId, payeeId: best.combo.payeeId, payeeName: best.combo.payeeName } : null;
  };
}

/** The payee a UPI narration names ("UPI-SWIGGY-swiggy@icici-..." → "Swiggy"), for rows with no history. Else null. */
export function guessPayee(narration: string): string | null {
  const n = narration.trim();
  // HDFC/Axis: "UPI-NAME-handle-...". SBI: "TO TRANSFER-UPI/DR/<ref>/NAME/BANK/handle/...".
  const m = /^UPI-([^-@]{2,40})-/i.exec(n) ?? /^(?:TO|BY) TRANSFER-UPI\/(?:DR|CR)\/\d+\/([^/@]{2,40})\//i.exec(n);
  const name = m?.[1]?.trim();
  if (!name || /\d{4,}/.test(name) || /^(dr|cr)$/i.test(name)) return null;
  return name.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** Payment mode from the narration, limited to what the business allows. */
export function guessMode(narration: string, allowed: readonly string[]): string {
  const n = narration.toLowerCase();
  const want = /^upi|\bupi[-/]/.test(n) ? "upi" : /\b(neft|imps|rtgs|nach|ach)\b/.test(n) ? "bank_transfer" : /\batm\b|cash wd|cash withdrawal/.test(n) ? "cash" : /\bpos\b|debit card|\bcard\b/.test(n) ? "card" : /\bchq\b|cheque|\bclg\b/.test(n) ? "cheque" : "bank_transfer";
  return allowed.includes(want) ? want : (allowed[0] ?? want);
}
